import axios from "axios";
import * as Sentry from "@sentry/react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

const TOKEN_KEY = "trendrise_token";
const USER_KEY = "trendrise_user";
const PROJECT_KEY = "trendrise_active_project";

// Backwards-compat migration for any existing sessions from the previous brand.
try {
  const legacyToken = localStorage.getItem("readrise_token");
  if (legacyToken && !localStorage.getItem(TOKEN_KEY)) {
    localStorage.setItem(TOKEN_KEY, legacyToken);
    localStorage.removeItem("readrise_token");
  }
  const legacyUser = localStorage.getItem("readrise_user");
  if (legacyUser && !localStorage.getItem(USER_KEY)) {
    localStorage.setItem(USER_KEY, legacyUser);
    localStorage.removeItem("readrise_user");
  }
  const legacyProject = localStorage.getItem("readrise_active_project");
  if (legacyProject && !localStorage.getItem(PROJECT_KEY)) {
    localStorage.setItem(PROJECT_KEY, legacyProject);
    localStorage.removeItem("readrise_active_project");
  }
} catch (_) {
  /* localStorage may be unavailable in SSR/tests */
}

export const STORAGE_KEYS = { TOKEN_KEY, USER_KEY, PROJECT_KEY };

const api = axios.create({ baseURL: API });

api.interceptors.request.use((config) => {
  const token = localStorage.getItem(TOKEN_KEY);
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

api.interceptors.response.use(
  (r) => r,
  (err) => {
    const status = err?.response?.status;
    // Report only genuine server-side / network failures to Sentry. 4xx
    // (validation, auth) is expected and would otherwise pollute alerts.
    if (!status || status >= 500) {
      try {
        Sentry.withScope((scope) => {
          scope.setTag("source", "axios");
          scope.setTag("http_status", String(status || "network"));
          const rawUrl = err?.config?.url || "";
          scope.setContext("request", {
            method: err?.config?.method,
            // Drop query string — it can carry secrets.
            url: rawUrl.split("?")[0],
          });
          Sentry.captureException(err);
        });
      } catch (_e) {
        /* Sentry not initialised — no-op */
      }
    }
    if (status === 401) {
      localStorage.removeItem(TOKEN_KEY);
      localStorage.removeItem(USER_KEY);
      const path = window.location.pathname;
      const publicPaths = ["/", "/login", "/signup", "/pricing", "/forgot-password", "/reset-password"];
      const isPublic = publicPaths.some((p) => path === p || path.startsWith(`${p}/`)) || path.startsWith("/r/");
      if (!isPublic) {
        window.location.href = "/login";
      }
    }
    return Promise.reject(err);
  }
);

export const CHOOSE_PLAN_MESSAGE = "Choose a plan to unlock AI-powered actions.";

// Human-readable message for billing/usage rejections (402 / 409 / 429 / 503). Never exposes internals.
export function billingErrorMessage(err) {
  const status = err?.response?.status;
  const detail = err?.response?.data?.detail;
  if (detail && typeof detail === "object") {
    if (detail.code === "billing_status_blocked" && detail.entitlement_reason === "no_subscription") return CHOOSE_PLAN_MESSAGE;
    if (typeof detail.message === "string") return detail.message;
  }
  if (typeof detail === "string" && [402, 409, 429, 503].includes(status)) return detail;
  return null;
}

export default api;
